import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { isNoDeskError, readDeskPrograms, type DeskProgramRef } from '../utils/partnerReferrals';

/**
 * Who has a "My referral program" desk (docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md
 * §16): partnerDeskGetContext on the tenant door lists the active programs
 * the caller's company operates and the caller's capabilities. Kept apart
 * from the desk adapter (usePartnerDesk.ts) so the side nav does not pull the
 * desk UI package into its bundle.
 */

type Call = (name: string, data: Record<string, unknown>) => Promise<unknown>;

const callByName: Call = async (name, data) => (await httpsCallable(functions, name)(data)).data;

export interface PartnerDeskAccess {
  /** 'idle' when the caller is not eligible to ask (no request is made). */
  status: 'idle' | 'loading' | 'ready' | 'error';
  programs: DeskProgramRef[];
  /** Server capability strings (shared-schemas PARTNER_DESK_CAPABILITIES). */
  capabilities: string[];
  refresh: () => void;
}

interface DeskContextResult {
  programs: DeskProgramRef[];
  capabilities: string[];
}

/**
 * One partnerDeskGetContext request per signed-in identity, shared by the
 * side nav, the route guard and the page (all mount together). A refusal
 * ("no desk for you") resolves to no programs; any other failure is not
 * cached, so the next mount asks again.
 */
const contextCache = new Map<string, Promise<DeskContextResult>>();

export function fetchPartnerDeskContext(cacheKey: string, call: Call = callByName): Promise<DeskContextResult> {
  const hit = contextCache.get(cacheKey);
  if (hit) return hit;
  const p = call('partnerDeskGetContext', {})
    .then((res) => {
      const caps = (res as { capabilities?: unknown } | null)?.capabilities;
      return {
        programs: readDeskPrograms(res),
        capabilities: Array.isArray(caps) ? caps.filter((x): x is string => typeof x === 'string') : [],
      };
    })
    .catch((err: unknown) => {
      if (isNoDeskError(err)) return { programs: [], capabilities: [] };
      contextCache.delete(cacheKey);
      throw err;
    });
  contextCache.set(cacheKey, p);
  return p;
}

/** Test seam and sign-out hygiene. */
export function clearPartnerDeskContextCache(): void {
  contextCache.clear();
}

/**
 * The programs this caller may work, and their capabilities. `cacheKey` is
 * null when the caller is not eligible to ask (canOpenPartnerDesk false):
 * no request, no programs.
 */
export function usePartnerDeskAccess(cacheKey: string | null): PartnerDeskAccess {
  const [state, setState] = useState<{ key: string | null; status: PartnerDeskAccess['status']; result: DeskContextResult }>({
    key: null,
    status: 'idle',
    result: { programs: [], capabilities: [] },
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!cacheKey) {
      setState({ key: null, status: 'idle', result: { programs: [], capabilities: [] } });
      return;
    }
    let active = true;
    setState((s) => ({ key: cacheKey, status: 'loading', result: s.key === cacheKey ? s.result : { programs: [], capabilities: [] } }));
    fetchPartnerDeskContext(cacheKey)
      .then((result) => {
        if (active) setState({ key: cacheKey, status: 'ready', result });
      })
      .catch(() => {
        if (active) setState({ key: cacheKey, status: 'error', result: { programs: [], capabilities: [] } });
      });
    return () => {
      active = false;
    };
  }, [cacheKey, nonce]);

  const refresh = useCallback(() => {
    if (cacheKey) contextCache.delete(cacheKey);
    setNonce((n) => n + 1);
  }, [cacheKey]);

  // Before the effect runs for a new key, report loading rather than a stale answer.
  const current = state.key === cacheKey;
  const status: PartnerDeskAccess['status'] = !cacheKey ? 'idle' : current ? state.status : 'loading';
  return {
    status,
    programs: current ? state.result.programs : [],
    capabilities: current ? state.result.capabilities : [],
    refresh,
  };
}
